import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj) return null;
  return {
    id: obj.id,
    sellerId: obj.seller_id,
    code: obj.code,
    description: obj.description,
    discountType: obj.discount_type,
    discountValue: obj.discount_value,
    appliesTo: obj.applies_to,
    applicableItemIds: obj.applicable_item_ids || [],
    minPurchaseAmount: obj.min_purchase_amount,
    maxDiscountAmount: obj.max_discount_amount,
    startDate: obj.start_date,
    endDate: obj.end_date,
    usageLimit: obj.usage_limit,
    usedCount: obj.used_count,
    isActive: obj.is_active,
    createdAt: obj.created_at,
    updatedAt: obj.updated_at
  };
};

const toSnakeCase = (obj) => {
  if (!obj) return null;
  return {
    seller_id: obj.sellerId,
    code: obj.code,
    description: obj.description,
    discount_type: obj.discountType,
    discount_value: obj.discountValue,
    applies_to: obj.appliesTo,
    applicable_item_ids: obj.applicableItemIds,
    min_purchase_amount: obj.minPurchaseAmount,
    max_discount_amount: obj.maxDiscountAmount,
    start_date: obj.startDate,
    end_date: obj.endDate,
    usage_limit: obj.usageLimit,
    is_active: obj.isActive
  };
};

export const couponService = {
  async getMyCoupons() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: sellerAccount } = await supabase
      .from('seller_accounts')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (!sellerAccount) throw new Error('Seller account not found');

    const { data, error } = await supabase
      .from('coupons')
      .select('*')
      .eq('seller_id', sellerAccount.id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data.map(toCamelCase);
  },

  async getAllCoupons() {
    const { data, error } = await supabase
      .from('coupons')
      .select('*, seller_accounts(business_name)')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data.map(row => ({
      ...toCamelCase(row),
      sellerName: row.seller_accounts?.business_name
    }));
  },

  async create(couponData) {
    const { data, error } = await supabase
      .from('coupons')
      .insert(toSnakeCase(couponData))
      .select()
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async update(id, couponData) {
    const { data, error } = await supabase
      .from('coupons')
      .update(toSnakeCase(couponData))
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return toCamelCase(data);
  },

  async delete(id) {
    const { error } = await supabase
      .from('coupons')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async validateCoupon(code, sellerId = null) {
    let query = supabase
      .from('coupons')
      .select('*')
      .eq('code', code)
      .eq('is_active', true);
    
    if (sellerId) {
      query = query.eq('seller_id', sellerId);
    }

    const { data, error } = await query.maybeSingle();

    if (error) throw error;
    if (!data) return { valid: false, message: 'Invalid coupon code' };

    // Check dates
    const now = new Date();
    if (data.start_date && new Date(data.start_date) > now) {
      return { valid: false, message: 'Coupon is not yet active' };
    }
    if (data.end_date && new Date(data.end_date) < now) {
      return { valid: false, message: 'Coupon has expired' };
    }

    // Check usage limit
    if (data.usage_limit && data.used_count >= data.usage_limit) {
      return { valid: false, message: 'Coupon usage limit reached' };
    }

    return { valid: true, coupon: toCamelCase(data) };
  }
};
